import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';

/** Recall "confirm a pulled batch" checklist — the mobile equivalent of
 * the till's shelf recall banner. Shows every recalled batch still on this
 * branch's shelf; one tap marks a batch pulled (POST /recall-returns/pulls,
 * already idempotent server-side). */
export default function StaffAppRecallSheet() {
  const [items, setItems] = useState(null);
  const [pulling, setPulling] = useState('');

  const load = () => api.get('/recall-returns/on-shelf').then((res) => setItems(res.data.items || []));
  useEffect(() => { load(); }, []);

  const pull = async (productId, batchNumber) => {
    const key = `${productId}|${batchNumber}`;
    setPulling(key);
    try {
      await api.post('/recall-returns/pulls', { product_id: productId, batch_number: batchNumber || '' });
      toast.success('Marked as pulled from the shelf');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not record this pull');
    } finally { setPulling(''); }
  };

  if (!items) return <Loader2 className="w-5 h-5 animate-spin text-muted-foreground mx-auto my-6" />;
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground py-6 text-center" data-testid="staff-app-recall-empty">Nothing recalled left on this branch's shelf.</p>;
  }

  return (
    <div className="space-y-2" data-testid="staff-app-recall-list">
      {items.map((item) => item.batches.map((b) => {
        const key = `${item.product_id}|${b.batch_number}`;
        return (
          <div key={key} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5" data-testid={`staff-app-recall-row-${key}`}>
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">{item.name}</p>
              <p className="text-xs text-muted-foreground">{b.batch_number ? `Batch ${b.batch_number} · ` : ''}{b.quantity} unit(s)</p>
            </div>
            {b.pulled ? (
              <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600 shrink-0" data-testid={`staff-app-recall-pulled-${key}`}>
                <CheckCircle2 className="w-4 h-4" /> Pulled
              </span>
            ) : (
              <Button size="sm" className="shrink-0" disabled={pulling === key} onClick={() => pull(item.product_id, b.batch_number)} data-testid={`staff-app-recall-pull-${key}`}>
                {pulling === key ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Mark pulled'}
              </Button>
            )}
          </div>
        );
      }))}
    </div>
  );
}
